import { useCallback, useMemo, useSyncExternalStore } from "react";
import { placeContentSchema, type PlaceContent } from "@vmeste/shared";
import { t } from "../i18n/index.js";
import { operations } from "../lib/outbox.js";
import { syncEngine } from "../lib/sync.js";
import { plaintextOf } from "../lib/plaintext.js";
import { toPlaceViews } from "../lib/views.js";
import type { PlaceView } from "./views.js";

/**
 * Places on the map (SPEC §4.10).
 *
 * The shape is the one wishes and lists already have: every write goes through
 * the outbox and draws immediately, and the codec of the space being written
 * into is read before any of it — `null` means the space is encrypted and this
 * device has no key, and then nothing is written at all. Plaintext in a space
 * that is meant to be encrypted is far worse than a refused write.
 *
 * What is *not* here is anything geographic. Distance, «что рядом», the order
 * pins are drawn in — all of that is `lib/geo.ts` and the screen, because none
 * of it can be asked of the server: the coordinates are inside the blob, and
 * the reason they are is that no browser will report a position to a server
 * while the phone is in a pocket anyway.
 */

/** A push body is short by nature; the rest is in the app. */
const PREVIEW_BODY_MAX = 120;

const clamp = (text: string): string =>
  text.length <= PREVIEW_BODY_MAX ? text : `${text.slice(0, PREVIEW_BODY_MAX - 1)}…`;

export interface NewPlace {
  title: string;
  lat: number;
  lng: number;
  address?: string;
  note?: string;
  url?: string;
  /** Somewhere they have already been, added after the fact. */
  visited?: boolean;
}

/** The fields an edit may change. Coordinates included: a pin can be dragged. */
export interface PlaceEdit {
  title?: string;
  address?: string;
  note?: string;
  url?: string;
  lat?: number;
  lng?: number;
}

export interface PlaceActions {
  places: PlaceView[];
  /** Null until the first sync lands, so a screen can tell empty from unknown. */
  loading: boolean;
  add(input: NewPlace): string | null;
  edit(placeId: string, patch: PlaceEdit): void;
  setVisited(placeId: string, visited: boolean): void;
  remove(placeId: string): void;
  /** A score, and words if there were any. Saying it again is a correction. */
  review(placeId: string, rating: number, text?: string): void;
  unreview(placeId: string): void;
}

/**
 * What the other person sees on their lock screen: a name, not an id.
 *
 * Read from the members already synced, exactly as the chat and the wishes do.
 */
function nameOf(spaceId: string, self: string | null): string {
  const name = syncEngine
    .view(spaceId)
    .members.find((member) => member.userId === self)?.user.displayName;

  return name ?? t("chat.someone");
}

/** The content of a place as it is stored, for an edit that changes part of it. */
function contentOf(spaceId: string, placeId: string): PlaceContent | null {
  const blob = syncEngine.view(spaceId).places[placeId]?.content;
  if (!blob) return null;

  const parsed = placeContentSchema.safeParse(plaintextOf(blob));
  return parsed.success ? parsed.data : null;
}

export function usePlaces(spaceId: string | null, self: string | null): PlaceActions {
  const snapshot = useSyncExternalStore(syncEngine.subscribe, syncEngine.getSnapshot);

  const places = useMemo(
    () => (spaceId ? toPlaceViews(syncEngine.view(spaceId)) : []),
    [spaceId, snapshot],
  );

  const add = useCallback(
    (input: NewPlace): string | null => {
      if (!spaceId) return null;

      const title = input.title.trim();
      if (!title) return null;

      const codec = syncEngine.codec(spaceId);
      if (!codec) return null;

      const address = input.address?.trim();
      const note = input.note?.trim();
      const url = input.url?.trim();

      const content: PlaceContent = {
        title,
        lat: input.lat,
        lng: input.lng,
        ...(address ? { address } : {}),
        ...(note ? { note } : {}),
        ...(url ? { url } : {}),
      };

      /*
       * The id is minted here rather than inside the operation, because the
       * screen needs it now: a photograph is attached to the place the moment
       * it is added, and an attachment needs something to hang from.
       */
      const id = crypto.randomUUID();

      /*
       * The words of the notification are composed here, beside the place, and
       * go through the same codec — so an encrypted space forwards a blob the
       * server cannot read and the other device opens it (SPEC §3, decision 3).
       *
       * «Имя: название» rather than a sentence with a verb: Russian would need
       * to know whether that verb is «добавил» or «добавила», and a name does
       * not say.
       */
      void Promise.all([
        codec.encode(content),
        codec.encode({
          title: t("places.notifyAdded", { name: nameOf(spaceId, self) }),
          body: clamp([title, address].filter(Boolean).join(" · ")),
        }),
      ]).then(([blob, preview]) => {
        syncEngine.mutate(
          operations.createPlace(
            spaceId,
            { id, content: blob, visited: input.visited ?? false },
            { type: "place.added", targetType: "place", preview },
          ),
        );
      });

      return id;
    },
    [spaceId, self],
  );

  const edit = useCallback(
    (placeId: string, patch: PlaceEdit) => {
      if (!spaceId) return;

      const codec = syncEngine.codec(spaceId);
      const current = contentOf(spaceId, placeId);
      // Nothing to merge into: a device with no key must not rewrite a place
      // it cannot read, or the parts it cannot see would be dropped.
      if (!codec || !current) return;

      const next: PlaceContent = {
        ...current,
        ...(patch.title !== undefined ? { title: patch.title.trim() } : {}),
        ...(patch.lat !== undefined ? { lat: patch.lat } : {}),
        ...(patch.lng !== undefined ? { lng: patch.lng } : {}),
      };

      // Emptying a field removes it rather than storing "": the schema treats
      // absent and empty the same, and one of the two is a lie about intent.
      for (const field of ["address", "note", "url"] as const) {
        const value = patch[field];
        if (value === undefined) continue;
        if (value.trim()) next[field] = value.trim();
        else delete next[field];
      }

      if (!next.title) return;

      void codec.encode(next).then((content) => {
        syncEngine.mutate(operations.updatePlace(spaceId, placeId, { content }));
      });
    },
    [spaceId],
  );

  const setVisited = useCallback(
    (placeId: string, visited: boolean) => {
      if (!spaceId) return;
      syncEngine.mutate(operations.updatePlace(spaceId, placeId, { visited }));
    },
    [spaceId],
  );

  const remove = useCallback(
    (placeId: string) => {
      if (!spaceId) return;
      syncEngine.mutate(operations.deletePlace(spaceId, placeId));
    },
    [spaceId],
  );

  const review = useCallback(
    (placeId: string, rating: number, text?: string) => {
      if (!spaceId) return;

      const codec = syncEngine.codec(spaceId);
      if (!codec) return;

      const words = text?.trim();

      /*
       * Marking it visited comes with the opinion, and only ever forwards.
       * Rating a place you have not been to is not a thing anybody means to do,
       * and the alternative — a score on a pin drawn as «ещё не были» — is a
       * map that contradicts itself.
       */
      const place = syncEngine.view(spaceId).places[placeId];
      if (place && place.visitedAt === null) {
        syncEngine.mutate(operations.updatePlace(spaceId, placeId, { visited: true }));
      }

      if (!words) {
        // Explicitly none, not omitted: taking the words back is something a
        // person can mean, and the request schema distinguishes the two.
        syncEngine.mutate(operations.reviewPlace(spaceId, placeId, { rating, content: null }));
        return;
      }

      void codec.encode({ text: words }).then((content) => {
        syncEngine.mutate(operations.reviewPlace(spaceId, placeId, { rating, content }));
      });
    },
    [spaceId],
  );

  const unreview = useCallback(
    (placeId: string) => {
      if (!spaceId) return;
      syncEngine.mutate(operations.unreviewPlace(spaceId, placeId));
    },
    [spaceId],
  );

  return {
    places,
    loading: spaceId ? syncEngine.view(spaceId).revision === 0 : false,
    add,
    edit,
    setVisited,
    remove,
    review,
    unreview,
  };
}
